import { view, set, lensPath, pipe, identity, lensProp, isEmpty } from 'ramda';
import moment from 'moment';
import {
  streetName,
  address_line_2,
  address_prefix,
  city,
  county,
  houseName,
  houseNumber,
  postCode,
  intAddressLine1,
  intAddressLine2,
  intAddressLine3,
  intAddressLine4,
  countrySelect,
  dateMoved,
  addressHistoryDate,
  residentialStatus
} from '../constants/validation/addressDetails';
import { mapResidentialStatus } from '../Library/mapResidentialStatus';

export const ADDRESS_INVALID = 'ADDRESS_INVALID';
export const ADDRESS_TYPE_INVALID = 'ADDRESS_TYPE_INVALID';
export const HISTORY_INCOMPLETE = 'HISTORY_INCOMPLETE';

const dateComparisonGranularity = 'month';

const currentAddressLens = lensProp('ADDRESS');
const previousAddressesLens = lensProp(HISTORY_INCOMPLETE);
const residentialStatusLens = lensProp('residential_status');
const currentAddressEffectivePeriodFromLens = lensPath(['ADDRESS', 'effective_period', 'from']);
const getEffectiveFrom = view(lensPath(['effective_period', 'from']));

// Old app defs: src/js/utils/utils.js :: 49, 647
// Lookup in old app: _locale/en-GB/content.json :: 70,

export const alphabet = /^[a-z]+(([-']|\s)?[a-z]+)*$/i;
export const alphabetNumeric =
  /^((^[a-z]{2}\.\s)?((\w|\w')\s[a-z]{2}\.\s)?([a-z0-9]+-[a-z0-9]+)?(\/)?([a-z]{1}'[a-z]+|\s[a-z]{1}'[a-z]+|[a-z]'\s[a-z]+|[a-z]'[s]\s[a-z]+)*([a-z0-9]+\s{0,1})?)+(\w{2,}'|[\w]|\w{2,}'[s])$/i;

export const alphabetPeriod = /^[a-z]+(([-'.]|[.]?\s)?[a-z]+)*$/i;

// https://assets.publishing.service.gov.uk/government/uploads/system/uploads/attachment_data/file/488478/Bulk_Data_Transfer_-_additional_validation_valid_from_12_November_2015.pdf
const postcode =
  /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([AZa-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$/;

const ukValidationChecks = {
  address_line_1: () => true, // supplied by API call so always valid
  address_line_2: (value = '') => value === '' || alphabetNumeric.test(value),
  address_prefix: (value = '') => value === '' || alphabetNumeric.test(value),
  city: (value = '') => value && alphabetPeriod.test(value),
  county: (value = '') => value === '' || alphabet.test(value),
  house_name: (value = '', uk_address) =>
    (value !== '' ? alphabet.test(value) : alphabetNumeric.test(uk_address.house_number || '')) ||
    value === '',
  house_number: (value = '', uk_address) => {
    if (value !== '') {
      return alphabetNumeric.test(value);
    }

    if (uk_address.organisation_name && uk_address.organisation_name !== '') {
      return true;
    }

    return alphabet.test(uk_address.house_name || '');
  },
  postal_code: (value = '') => postcode.test(value),
  street_name: (value = '') =>
    alphabetNumeric.test(value) || value === '' /** QR-686 allow null street_name */
};

const mappedUkValidationMessages = {
  address_line_1: streetName,
  address_line_2: address_line_2,
  address_prefix: address_prefix,
  city: city,
  county: county,
  house_name: houseName,
  house_number: houseNumber,
  postal_code: postCode,
  street_name: streetName
};

const internationalValidationChecks = {
  address_line_1: (value = '') => value && alphabetNumeric.test(value),
  address_line_2: (value = '') => value === '' || alphabetNumeric.test(value),
  address_line_3: (value = '') => value === '' || alphabetNumeric.test(value),
  address_line_4: (value = '') => value === '' || alphabetNumeric.test(value),
  country: (value = '') => value !== 'Please select a country' && Boolean(value)
};

const mappedInternationalValidationMessages = {
  address_line_1: intAddressLine1,
  address_line_2: intAddressLine2,
  address_line_3: intAddressLine3,
  address_line_4: intAddressLine4,
  country: countrySelect
};

/**
 * @typedef {object} PersonalDetails
 * @property {string} [birthDate]
 */

/**
 * @typedef {object} ValidationContext
 * @property {import('moment').Moment} minimumDate
 */

/**
 * @param {string} value
 * @param {import('moment').Moment | null} [defaultValue]
 * @returns {import('moment').Moment | null}
 */
const getDate = (value, defaultValue = null) => (value ? moment(value, 'YYYY-MM') : defaultValue);

/**
 * @param {import('../components/AddressDetails/types').CustomerAddress} address
 * @returns {object | undefined}
 */
function getAddressValidationError(address) {
  if (address) {
    const { uk_address, international_address } = address;

    if (uk_address && !isEmpty(uk_address)) {
      const errors = {};

      Object.keys(ukValidationChecks).forEach((key) => {
        /* re-map 'street' keys (from existing customer data) to 'street_name' field, otherwise use original key  */
        const currentKey = key === 'street' ? 'street_name' : key;

        const value =
          key === 'street_name' && uk_address.street ? uk_address.street : uk_address[key] || '';

        /* if validation checks for the current key don't return anything. (???) */
        if (!ukValidationChecks[currentKey](value, uk_address)) {
          // throw error if house name and house number both empty
          if (currentKey === 'house_name' || currentKey === 'house_number') {
            if (
              ((!uk_address.house_name || uk_address.house_name.trim() === '') &&
                (!uk_address.house_number || uk_address.house_number.trim() === '')) ||
              ((!uk_address.house_number || uk_address.house_number.trim() === '') &&
                (!uk_address.house_name || uk_address.house_name.trim() === ''))
            ) {
              errors[currentKey] = mappedUkValidationMessages[currentKey];
            }
          } else {
            errors[currentKey] = mappedUkValidationMessages[currentKey];
          }
        }
      });

      if (Object.keys(errors).length > 0) {
        return {
          ADDRESS_INVALID: postCode,
          uk_address: errors
        };
      }

      return undefined;
    }
    if (international_address && !isEmpty(international_address)) {
      const errors = {};

      Object.keys(internationalValidationChecks).forEach((key) => {
        const value = key.includes('address_line')
          ? international_address[key] || international_address[key.replace(/_([^_]*)$/, '$1')]
          : international_address[key] || '';

        if (!internationalValidationChecks[key](value, international_address)) {
          errors[key] = mappedInternationalValidationMessages[key];
        }
      });

      return Object.keys(errors).length > 0
        ? {
            international_address: errors
          }
        : undefined;
    }
  }

  return {
    [ADDRESS_INVALID]: postCode
  };
}

/**
 * @param {import('../components/AddressDetails/types').CustomerAddressHistory} state
 */
const getAddresses = ({ ADDRESS, PREVIOUS_ADDRESSES = [] }) => [ADDRESS, ...PREVIOUS_ADDRESSES];

/**
 * @param {import('../components/AddressDetails/types').CustomerAddress} address
 */
function applyValidateCurrentAddressValue(address) {
  const addressError = getAddressValidationError(address);
  if (addressError) {
    return set(currentAddressLens, addressError);
  }

  return identity;
}

/**
 * @param {import('../components/AddressDetails/types').CustomerAddressHistory} state
 * @param {ValidationContext} context
 */
function applyValidateCurrentEffectivePeriod(state, { minimumDate }) {
  const { ADDRESS: address } = state;
  if (address && address.effective_period && address.effective_period.from) {
    const { from } = address.effective_period;

    /**
     * @todo first test if teh values for month and year are even valid.
     * from is a string of `YYYY-mm`
     */
    const [yearString, monthString] = from.split('-');
    const month = parseInt(monthString);
    const year = parseInt(yearString);

    if (month < 1 || month > 12 || year < 1) {
      /** return basic failure message for validating date */
      return set(currentAddressEffectivePeriodFromLens, dateMoved);
    }

    const fromValue = getDate(from);

    if (
      fromValue.isSameOrBefore(moment(), dateComparisonGranularity) &&
      fromValue.isSameOrAfter(minimumDate, dateComparisonGranularity)
    ) {
      return identity;
    } else {
      return set(currentAddressEffectivePeriodFromLens, addressHistoryDate);
    }
  }

  return set(currentAddressEffectivePeriodFromLens, dateMoved);
}

/**
 * @param {import('../components/AddressDetails/types').CustomerAddressHistory} state
 * @param {ValidationContext} context
 */
function applyValidateCurrentAddress(state, context) {
  return pipe(
    applyValidateCurrentAddressValue(state.ADDRESS),
    applyValidateCurrentEffectivePeriod(state, context),
    applyValidateAddressHistoryComplete(state, context)
  );
}
/**
 * @param {import('../components/AddressDetails/types').CustomerAddressHistory} state
 * @param {ValidationContext} context
 */
function applyValidatePreviousAddresses(state, context) {
  return pipe(
    applyValidatePreviousAddressValid(state),
    applyValidatePreviousEffectivePeriods(state, context),
    applyValidateAddressHistoryComplete(state, context)
  );
}

/**
 * @param {import('../components/AddressDetails/types').CustomerAddressHistory} state
 */
function applyValidatePreviousAddressValid(state) {
  if (state.PREVIOUS_ADDRESSES && state.PREVIOUS_ADDRESSES.length > 0) {
    /**
     * @param {number} index
     * @param {string} error
     */
    const setAtIndex = (index, error) => set(lensPath(['PREVIOUS_ADDRESSES', index]), error);

    const validators = state.PREVIOUS_ADDRESSES.map((address, index) => {
      // get validation for the individual address
      const addressError = getAddressValidationError(address);
      if (addressError) {
        return setAtIndex(index, addressError);
      }

      return identity;
    });

    if (validators.length > 0) {
      // @ts-ignore
      return pipe(...validators);
    }
  }

  return identity;
}

/**
 * @param {import('../components/AddressDetails/types').CustomerAddressHistory} state
 * @param {number} yearsToCapture
 */
const getIsHistoryComplete = (state, yearsToCapture = 3) => {
  const startOfCapturedRange = moment
    .min(
      getAddresses(state)
        .filter((address) => address.effective_period && address.effective_period.from)
        .map((address) => moment(address.effective_period.from, 'YYYY-MM'))
    )
    .startOf('month');

  const requiredStartOfRange = moment().startOf('month').subtract(yearsToCapture, 'years');

  return startOfCapturedRange.isSameOrBefore(requiredStartOfRange, 'month');
};

/**
 * @param {import('../components/AddressDetails/types').CustomerAddressHistory} state
 * @param {ValidationContext} context
 */
function applyValidateAddressHistoryComplete(state, context) {
  // Don't apply history error if any dates are invalid
  const errors = pipe(
    applyValidateCurrentEffectivePeriod(state, context),
    applyValidatePreviousEffectivePeriods(state, context)
  )({});

  // If there are other errors in address, don't validate history
  if (Object.keys(errors).length !== 0 || getIsHistoryComplete(state)) {
    return identity;
  }

  return set(previousAddressesLens, true);
}

/**
 * @param {import('../components/AddressDetails/types').CustomerAddressHistory} state
 * @param {ValidationContext} context
 */
function applyValidatePreviousEffectivePeriods(state, { minimumDate }) {
  const currentFrom = getDate(view(currentAddressEffectivePeriodFromLens, state), moment());

  if (state.PREVIOUS_ADDRESSES && state.PREVIOUS_ADDRESSES.length > 0) {
    /**
     * Set a validation method fo a specific previous address that has an invalid `effective_period.from`
     * @param {number} index
     */
    const setAtIndex = (index) =>
      set(lensPath(['PREVIOUS_ADDRESSES', index, 'effective_period', 'from']), dateMoved);

    const validators = state.PREVIOUS_ADDRESSES.map((address, index, addresses) => {
      const thisDate = getDate(getEffectiveFrom(address));
      const prevDate = index === 0 ? currentFrom : getDate(getEffectiveFrom(addresses[index - 1]));

      if (
        thisDate &&
        thisDate.isBefore(prevDate, dateComparisonGranularity) &&
        thisDate.isSameOrAfter(minimumDate, dateComparisonGranularity)
      ) {
        return identity;
      }

      return setAtIndex(index);
    });

    if (validators.length > 0) {
      // @ts-ignore
      return pipe(...validators);
    }
  }

  return identity;
}

/**
 * @param {import('../components/AddressDetails/types').CustomerAddressHistory} state
 */
function applyValidateResidentialStatus(state) {
  if (state && state.residential_status) {
    if (mapResidentialStatus(state.residential_status) !== '') {
      return identity;
    }
  }

  return set(residentialStatusLens, residentialStatus);
}

/**
 * @param {string} birthDate
 */
function getMinimumDate(birthDate) {
  return birthDate ? moment(birthDate, 'YYYY-MM') : moment('1800-01'); // arbitrary old date
}

/**
 * @param {import('../components/AddressDetails/types').CustomerAddressHistory} model
 * @param {PersonalDetails} personalDetails
 * @returns {object}
 */
export const validateModel = (model, { birthDate }) => {
  /** @type {ValidationContext} */
  const context = {
    minimumDate: getMinimumDate(birthDate)
  };

  return pipe(
    applyValidateCurrentAddress(model, context),
    applyValidatePreviousAddresses(model, context),
    applyValidateResidentialStatus(model)
  )({});
};

/**
 * @param {keyof import('../components/AddressDetails/types').CustomerAddressHistory} field
 * @param {import('../components/AddressDetails/types').CustomerAddressHistory} model
 * @param {PersonalDetails} personalDetails
 * @param {boolean} hasValidatedModel
 * @param {object} validationModel
 */
export const validateFormField = (
  field,
  model,
  { birthDate },
  hasValidatedModel,
  validationModel
) => {
  /** @type {PersonalDetails} */
  const context = {
    birthDate
  };

  const fullNewModelErrors = validateModel(model, context);
  if (hasValidatedModel) {
    return fullNewModelErrors;
  }

  switch (field) {
    case 'ADDRESS':
    case 'PREVIOUS_ADDRESSES':
      return {
        ...validationModel,
        [HISTORY_INCOMPLETE]: fullNewModelErrors[HISTORY_INCOMPLETE]
      };

    case 'residential_status':
      return {
        ...validationModel,
        residential_status: fullNewModelErrors.residential_status
      };

    default:
      return validationModel;
  }
};
